import React, { useState, useEffect, useRef, useContext } from 'react';
import { AuthContext } from '../App';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, Search, Mail, FileText, Clock, CheckCircle, XCircle, Download, Trash2, ChevronDown, MoreHorizontal } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const PAGE_SIZE = 50;

const Envelopes = () => {
  const { API, accessToken, orgId } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'all';
  
  const [envelopes, setEnvelopes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Download Options modal
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadTarget, setDownloadTarget] = useState(null); // { id, title }
  const [downloadOption, setDownloadOption] = useState('signed');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Sorting
  const [sortCol, setSortCol] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMailModal, setShowMailModal] = useState(false);
  const [mailTo, setMailTo] = useState('');
  const [mailSubject, setMailSubject] = useState('');
  const [mailMessage, setMailMessage] = useState('');
  const [isBulkLoading, setIsBulkLoading] = useState(false);

  // Remind
  const [remindingId, setRemindingId] = useState(null);

  // Row "···" dropdown menu
  const [openMenuId, setOpenMenuId] = useState(null);

  // Signer details popup (SignEasy-style)
  const [signerPopup, setSignerPopup] = useState(null); // { envelopeId, top, left, data: null | { envelope, org } }
  const [isLoadingPopup, setIsLoadingPopup] = useState(false);

  // Edit Recipient Email flow
  const [showEditEmailModal, setShowEditEmailModal] = useState(false);
  const [editEmailTarget, setEditEmailTarget] = useState(null); // { envelope, recipient }
  const [newEmailValue, setNewEmailValue] = useState('');
  const [showReviewChangesModal, setShowReviewChangesModal] = useState(false);
  const [isSendingEmailUpdate, setIsSendingEmailUpdate] = useState(false);

  const tabs = [
    { key: 'all', label: 'All', icon: Send, filter: null },
    { key: 'drafts', label: 'Drafts', icon: FileText, filter: 'draft' },
    { key: 'in-progress', label: 'In-Progress', icon: Clock, filter: 'sent' },
    { key: 'completed', label: 'Completed', icon: CheckCircle, filter: 'completed' },
    { key: 'declined', label: 'Declined', icon: XCircle, filter: 'declined' },
  ];

  const [tabCounts, setTabCounts] = useState({
    all: 0,
    drafts: 0,
    'in-progress': 0,
    completed: 0,
    declined: 0
  });

  // Pagination + stale-response guards
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const envelopesRequestSeqRef = useRef(0);
  const countsRequestSeqRef = useRef(0);

  // Re-fetch the first page when the active tab or sort changes
  useEffect(() => {
    fetchEnvelopes();
  }, [activeTab, sortCol, sortDir]);

  useEffect(() => {
    fetchTabCounts();
  }, [activeTab]);

  // Debounced re-fetch when the search query changes (300 ms delay); initial load is handled above
  const searchDebounceRef = useRef(null);
  const isFirstSearchRunRef = useRef(true);
  useEffect(() => {
    if (isFirstSearchRunRef.current) {
      isFirstSearchRunRef.current = false;
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchEnvelopes();
      fetchTabCounts();
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchQuery]);

  // Close signer popup on Escape key
  useEffect(() => {
    if (!signerPopup) return;
    const handleKey = (e) => { if (e.key === 'Escape') setSignerPopup(null); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [signerPopup]);

  // Lazy-load signer details when status chip is clicked
  const handleStatusClick = async (e, envelope) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    // Position popup below the clicked element
    const top = rect.bottom + window.scrollY + 6;
    const left = rect.left + window.scrollX;
    setSignerPopup({ envelopeId: envelope.id, top, left, data: null });
    setIsLoadingPopup(true);
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const [envRes, orgRes] = await Promise.all([
        axios.get(`${API}/envelopes/${envelope.id}`, { headers }),
        axios.get(`${API}/orgs/${orgId}`, { headers }),
      ]);
      setSignerPopup(prev =>
        prev && prev.envelopeId === envelope.id
          ? { ...prev, data: { envelope: envRes.data, org: orgRes.data } }
          : prev
      );
    } catch (err) {
      console.error('Failed to load signer details', err);
    } finally {
      setIsLoadingPopup(false);
    }
  };

  const fetchTabCounts = async () => {
    const requestId = ++countsRequestSeqRef.current;
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };

      // Fetch counts for all tabs in a single request (respects the current search)
      const params = new URLSearchParams();
      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      const response = await axios.get(
        `${API}/envelopes/counts${params.toString() ? '?' + params.toString() : ''}`,
        { headers }
      );
      if (requestId !== countsRequestSeqRef.current) return;

      const counts = response.data || {};
      setTabCounts({
        'all': counts.all || 0,
        'drafts': counts.draft || 0,
        'in-progress': counts.sent || 0,
        'completed': counts.completed || 0,
        'declined': counts.declined || 0
      });
    } catch (error) {
      console.error('Failed to fetch tab counts:', error);
    }
  };

  const fetchEnvelopes = async ({ append = false } = {}) => {
    const requestId = ++envelopesRequestSeqRef.current;
    try {
      if (append) setIsLoadingMore(true); else setIsLoading(true);
      const headers = { Authorization: `Bearer ${accessToken}` };
      const currentTab = tabs.find(t => t.key === activeTab);
      const statusFilter = currentTab?.filter ?? (activeTab === 'all' ? null : 'draft');

      // Build query string — search, sort and pagination are handled server-side across ALL records
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      params.set('sort', sortCol);
      params.set('dir', sortDir);
      // Request one extra row to detect whether another page exists
      params.set('limit', String(PAGE_SIZE + 1));
      const last = append ? envelopes[envelopes.length - 1] : null;
      if (last) {
        // Cursor paging: continue right after the last loaded row
        params.set('after_id', last.id);
        params.set('after_created_at', last.created_at);
        if (sortCol === 'title') params.set('after_value', last.title || '');
        else if (sortCol === 'status') params.set('after_value', last.status || '');
      } else {
        params.set('offset', '0');
      }
      const url = `${API}/envelopes?${params.toString()}`;
      const response = await axios.get(url, { headers });

      // Ignore responses for an outdated search / tab / sort
      if (requestId !== envelopesRequestSeqRef.current) return;

      // Validate response data
      if (Array.isArray(response.data)) {
        const page = response.data.slice(0, PAGE_SIZE);
        setHasMore(response.data.length > PAGE_SIZE);
        setEnvelopes(prev => {
          if (!append) return page;
          const seen = new Set(prev.map(e => e.id));
          return [...prev, ...page.filter(e => !seen.has(e.id))];
        });
      } else {
        console.error('Invalid response format:', response.data);
        setEnvelopes([]);
        setHasMore(false);
        toast.error('Invalid data format received');
      }
    } catch (error) {
      if (requestId !== envelopesRequestSeqRef.current) return;
      console.error('Failed to load envelopes:', error);
      toast.error('Failed to load envelopes');
      if (!append) {
        setEnvelopes([]);
        setHasMore(false);
      }
    } finally {
      if (requestId === envelopesRequestSeqRef.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  };

  const handleCreateEnvelope = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      await axios.post(
        `${API}/envelopes`,
        {
          title,
          recipients: [
            {
              email: recipientEmail,
              display_name: recipientName,
              role: 'Signer',
              routing_order: 1
            }
          ],
          variables: {}
        },
        { headers }
      );
      toast.success('Envelope created successfully!');
      setIsDialogOpen(false);
      setTitle('');
      setRecipientEmail('');
      setRecipientName('');
      fetchEnvelopes();
    } catch (error) {
      toast.error('Failed to create envelope');
    }
  };

  // Search is now handled server-side — the API already returns only matching records
  const filteredEnvelopes = envelopes;

  // Tab counts come from the server and already reflect the current search
  const displayTabCounts = tabCounts;

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'sent':
        return 'bg-blue-100 text-blue-700';
      case 'viewed':
        return 'bg-purple-100 text-purple-700';
      case 'draft':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getDraftBadge = (envelope) => {
    if (envelope.status !== 'draft') return null;
    
    // Check if recipients exists and is an array
    const hasRecipients = Array.isArray(envelope.recipients) && envelope.recipients.length > 0;
    return hasRecipients ? 
      { text: 'Recipients ready', color: 'bg-green-100 text-green-700' } :
      { text: 'Fields only', color: 'bg-orange-100 text-orange-700' };
  };

  const handleDownloadCertificate = async (envelopeId, title) => {
    try {
      const response = await axios.get(
        `${API}/envelopes/${envelopeId}/certificate`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Certificate_${title.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Certificate downloaded successfully');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download certificate');
    }
  };

  // Open the Download Options modal (completed envelopes only)
  const openDownloadModal = (envelope) => {
    setDownloadTarget({ id: envelope.id, title: envelope.title });
    setDownloadOption('signed');
    setShowDownloadModal(true);
  };

  // Download the stamped signed PDF
  const handleDownloadSignedDoc = async (envelopeId, title) => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      // Get the file_id for this envelope's document
      const docsRes = await axios.get(`${API}/envelopes/${envelopeId}/docs`, { headers });
      if (!docsRes.data || docsRes.data.length === 0) {
        toast.error('Signed document not available');
        return;
      }
      const fileId = docsRes.data[0].file_id;
      // Use plain /files endpoint — same as Detail page "Download Signed Document".
      // envelope_docs.file_id already points to the final signed PDF after completion;
      // the /download endpoint re-stamps field values causing [initials] artifacts.
      const fileRes = await axios.get(`${API}/files/${fileId}`, {
        headers,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([fileRes.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Signed_${title.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Signed document downloaded successfully');
    } catch (error) {
      console.error('Signed doc download failed:', error);
      toast.error('Signed document not available');
    }
  };

  // Download both signed PDF + certificate as a ZIP
  const handleDownloadBoth = async (envelopeId, title) => {
    try {
      const response = await axios.get(
        `${API}/envelopes/${envelopeId}/download-both`,
        { headers: { Authorization: `Bearer ${accessToken}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${title.replace(/\s+/g, '_')}_complete.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Documents downloaded as ZIP');
    } catch (error) {
      console.error('Both download failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to download documents');
    }
  };

  // Download both signed PDF + certificate merged into a single PDF
  const handleDownloadBothPdf = async (envelopeId, title) => {
    try {
      const response = await axios.get(
        `${API}/envelopes/${envelopeId}/download-both-pdf`,
        { headers: { Authorization: `Bearer ${accessToken}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${title.replace(/\s+/g, '_')}_complete.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Documents downloaded as a single PDF');
    } catch (error) {
      console.error('Both (PDF) download failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to download documents');
    }
  };

  // Confirm selection from the Download Options modal
  const handleDownloadConfirm = async () => {
    if (!downloadTarget) return;
    const { id, title } = downloadTarget;
    setShowDownloadModal(false);
    setDownloadTarget(null);
    if (downloadOption === 'signed') {
      await handleDownloadSignedDoc(id, title);
    } else if (downloadOption === 'certificate') {
      await handleDownloadCertificate(id, title);
    } else if (downloadOption === 'both-pdf') {
      await handleDownloadBothPdf(id, title);
    } else {
      await handleDownloadBoth(id, title);
    }
  };

  const handleNewEnvelopeClick = (e) => {
    if (false) {
      // placeholder
      navigate('/trial-expired');
    }
  };

  // ── Remind ───────────────────────────────────────────────────────────────
  const handleRemind = async (envelopeId) => {
    setRemindingId(envelopeId);
    try {
      await axios.post(`${API}/envelopes/${envelopeId}/remind`, {},
        { headers: { Authorization: `Bearer ${accessToken}` } });
      toast.success('Reminder sent');
    } catch {
      toast.error('Failed to send reminder');
    } finally {
      setRemindingId(null);
    }
  };

  // ── Edit Recipient Email ──────────────────────────────────────────────────
  const handleOpenEditEmail = async (envelope) => {
    setOpenMenuId(null);
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const res = await axios.get(`${API}/envelopes/${envelope.id}/recipients`, { headers });
      const pendingRecipient = (res.data || []).find(
        r => r.status !== 'completed' && !['cc', 'copy'].includes((r.role || '').toLowerCase())
      );
      if (!pendingRecipient) {
        toast.error('No pending recipient found');
        return;
      }
      setEditEmailTarget({ envelope, recipient: pendingRecipient });
      setNewEmailValue('');
      setShowEditEmailModal(true);
    } catch {
      toast.error('Failed to fetch recipients');
    }
  };

  const handleEmailInputNext = () => {
    const email = newEmailValue.trim();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setShowEditEmailModal(false);
    setShowReviewChangesModal(true);
  };

  const handleSendEmailUpdate = async () => {
    if (!editEmailTarget) return;
    setIsSendingEmailUpdate(true);
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      await axios.put(
        `${API}/envelopes/${editEmailTarget.envelope.id}/recipients/${editEmailTarget.recipient.id}/email`,
        { email: newEmailValue.trim() },
        { headers }
      );
      setShowReviewChangesModal(false);
      setEditEmailTarget(null);
      setNewEmailValue('');
      toast.success('Recipient email updated and invite sent');
    } catch {
      toast.error('Unable to update email');
    } finally {
      setIsSendingEmailUpdate(false);
    }
  };

  // ── Sorting ──────────────────────────────────────────────────────────────
  const handleSortClick = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  // Sorting is handled server-side so it applies across all records, not just the loaded page
  const sortedEnvelopes = filteredEnvelopes;

  // ── Selection ─────────────────────────────────────────────────────────────
  const toggleSelectAll = () => {
    if (selectedIds.size === sortedEnvelopes.length && sortedEnvelopes.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedEnvelopes.map(e => e.id)));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── Bulk handlers ─────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    setIsBulkLoading(true);
    let failed = 0;
    const headers = { Authorization: `Bearer ${accessToken}` };
    for (const id of selectedIds) {
      try {
        await axios.delete(`${API}/envelopes/${id}`, { headers });
      } catch (err) {
        console.error('Delete failed for', id, err);
        failed++;
      }
    }
    setIsBulkLoading(false);
    setShowDeleteModal(false);
    setSelectedIds(new Set());
    if (failed > 0) toast.error(`Failed to delete ${failed} document(s)`);
    else toast.success('Selected documents deleted');
    fetchEnvelopes();
    fetchTabCounts();
  };

  const handleBulkDownload = async () => {
    if (selectedIds.size === 0) { toast.error('Select at least one document'); return; }

    if (selectedIds.size === 1) {
      const [id] = selectedIds;
      const envelope = envelopes.find(e => e.id === id);
      handleDownloadCertificate(id, envelope?.title || 'document');
      return;
    }

    // Multiple — download as ZIP
    try {
      setIsBulkLoading(true);
      const response = await axios.post(
        `${API}/envelopes/bulk-download`,
        { envelope_ids: [...selectedIds] },
        { headers: { Authorization: `Bearer ${accessToken}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'documents.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Documents downloaded as ZIP');
    } catch (err) {
      console.error('Bulk download failed:', err);
      toast.error('Failed to download documents');
    } finally {
      setIsBulkLoading(false);
    }
  };

  const handleBulkMail = async () => {
    if (!mailTo.trim()) { toast.error('Please enter a recipient email'); return; }
    if (selectedIds.size === 0) { toast.error('Select at least one document'); return; }
    try {
      setIsBulkLoading(true);
      await axios.post(
        `${API}/envelopes/bulk-mail`,
        {
          envelope_ids: [...selectedIds],
          to_email: mailTo.trim(),
          subject: mailSubject.trim(),
          message: mailMessage.trim(),
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toast.success('Email sent successfully');
      setShowMailModal(false);
      setMailTo(''); setMailSubject(''); setMailMessage('');
    } catch (err) {
      console.error('Bulk mail failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setIsBulkLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="envelopes-page">

        {/* Page Title */}
        <h1 className="text-2xl font-semibold text-gray-900">All documents</h1>

        {/* Toolbar */}
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-1">
            {/* Action icons — enabled only when something is selected */}
            {selectedIds.size === 0 && (
              <span className="text-xs text-gray-400 mr-1 select-none">Select documents to enable actions</span>
            )}
            <button
              className={`p-2 rounded transition-colors ${selectedIds.size > 0 ? 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 cursor-pointer' : 'text-gray-300 cursor-not-allowed'}`}
              title={selectedIds.size > 0 ? 'Send by email' : 'Select at least one document'}
              onClick={() => { if (selectedIds.size > 0) setShowMailModal(true); }}
              disabled={selectedIds.size === 0}
            >
              <Mail size={18} />
            </button>
            <button
              className={`p-2 rounded transition-colors ${selectedIds.size > 0 ? 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 cursor-pointer' : 'text-gray-300 cursor-not-allowed'}`}
              title={selectedIds.size > 0 ? 'Download selected' : 'Select at least one document'}
              onClick={handleBulkDownload}
              disabled={selectedIds.size === 0 || isBulkLoading}
            >
              <Download size={18} />
            </button>
            <button
              className={`p-2 rounded transition-colors ${selectedIds.size > 0 ? 'text-gray-600 hover:text-red-600 hover:bg-red-50 cursor-pointer' : 'text-gray-300 cursor-not-allowed'}`}
              title={selectedIds.size > 0 ? 'Delete selected' : 'Select at least one document'}
              onClick={() => { if (selectedIds.size > 0) setShowDeleteModal(true); }}
              disabled={selectedIds.size === 0}
            >
              <Trash2 size={18} />
            </button>

            {/* Separator */}
            <div className="w-px h-6 bg-gray-200 mx-2" />

            {/* View dropdown — replaces tab bar, keeps same URL-based state */}
            <div className="relative border border-indigo-500 rounded px-1 py-0.5">
              <span className="absolute -top-2.5 left-2 text-[10px] text-indigo-500 bg-white px-1 leading-none">View</span>
              <select
                value={activeTab}
                onChange={(e) => { setSearchQuery(''); setSearchParams({ tab: e.target.value }); }}
                className="text-sm text-gray-700 appearance-none pl-1 pr-6 py-0.5 bg-transparent cursor-pointer focus:outline-none"
              >
                {tabs.map(tab => (
                  <option key={tab.key} value={tab.key}>
                    {tab.label} ({displayTabCounts[tab.key]})
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            </div>
          </div>

          {/* Search */}
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <Input
              type="text"
              placeholder="Search by document, signers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 text-sm"
              data-testid="search-envelopes"
            />
          </div>
        </div>

        {/* Document Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-visible">

          {/* Table Header */}
          <div className="grid items-center border-b border-gray-200 px-4 py-3 bg-gray-50"
            style={{ gridTemplateColumns: '44px 1fr 180px 210px 180px' }}>
            <div>
              <input
                type="checkbox"
                className="rounded border-gray-300 h-4 w-4 cursor-pointer"
                checked={sortedEnvelopes.length > 0 && selectedIds.size === sortedEnvelopes.length}
                onChange={toggleSelectAll}
                title="Select all"
              />
            </div>
            <button
              className="flex items-center gap-1 text-sm font-medium text-gray-600 select-none hover:text-indigo-600 text-left"
              onClick={() => handleSortClick('title')}
            >
              Document name
              <ChevronDown size={14} className={`transition-transform ${sortCol === 'title' && sortDir === 'asc' ? 'rotate-180' : ''} ${sortCol === 'title' ? 'text-indigo-500' : ''}`} />
            </button>
            <button
              className="flex items-center gap-1 text-sm font-medium text-gray-600 select-none hover:text-indigo-600 text-left"
              onClick={() => handleSortClick('status')}
            >
              Status
              <ChevronDown size={14} className={`transition-transform ${sortCol === 'status' && sortDir === 'asc' ? 'rotate-180' : ''} ${sortCol === 'status' ? 'text-indigo-500' : ''}`} />
            </button>
            <button
              className="flex items-center gap-1 text-sm font-medium text-gray-600 select-none hover:text-indigo-600 text-left"
              onClick={() => handleSortClick('created_at')}
            >
              Last modified
              <ChevronDown size={14} className={`transition-transform ${sortCol === 'created_at' && sortDir === 'asc' ? 'rotate-180' : ''} ${sortCol === 'created_at' ? 'text-indigo-500' : ''}`} />
            </button>
            <div className="text-sm font-medium text-gray-600">Actions</div>
          </div>

          {/* Table Body */}
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
            </div>
          ) : sortedEnvelopes.length > 0 ? (
            sortedEnvelopes.map((envelope) => {
              if (!envelope || !envelope.id || !envelope.title) {
                console.warn('Invalid envelope data:', envelope);
                return null;
              }

              // Use backend-provided signer_count (excludes CC recipients); fall back to recipient_names length
              const signerCount = envelope.signer_count != null
                ? Number(envelope.signer_count)
                : (Array.isArray(envelope.recipient_names) ? envelope.recipient_names.length : 0);
              const statusLabel = {
                completed: `Completed${signerCount > 0 ? ` by ${signerCount} signer${signerCount > 1 ? 's' : ''}` : ''}`,
                sent:      `In-Progress${signerCount > 0 ? ` · ${signerCount} signer${signerCount > 1 ? 's' : ''}` : ''}`,
                draft:     'Draft',
                declined:  'Declined',
              }[envelope.status] || envelope.status;

              const statusTextColor = {
                completed: 'text-green-600',
                sent:      'text-blue-600',
                draft:     'text-gray-500',
                declined:  'text-red-500',
              }[envelope.status] || 'text-gray-500';

              const lastModified = envelope.created_at
                ? new Date(envelope.created_at).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric',
                    hour: 'numeric', minute: '2-digit',
                  })
                : 'N/A';

              return (
                <div
                  key={envelope.id}
                  className="grid items-center border-b border-gray-100 px-4 py-4 hover:bg-gray-50 transition-colors"
                  style={{ gridTemplateColumns: '44px 1fr 180px 210px 180px' }}
                  data-testid={`envelope-${envelope.id}`}
                >
                  {/* Checkbox */}
                  <div>
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 h-4 w-4 cursor-pointer"
                      checked={selectedIds.has(envelope.id)}
                      onChange={() => toggleSelect(envelope.id)}
                    />
                  </div>

                  {/* Document name */}
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{envelope.title}</p>
                    {Array.isArray(envelope.recipient_emails) && envelope.recipient_emails.length > 0 && (
                      <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">
                        {envelope.recipient_emails.join(', ')}
                      </p>
                    )}
                  </div>

                  {/* Status */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {envelope.status === 'sent' ? (
                      /* Clickable for In-Progress: opens signer details popup */
                      <button
                        onClick={(e) => handleStatusClick(e, envelope)}
                        className={`text-sm font-medium ${statusTextColor} hover:underline cursor-pointer bg-transparent border-0 p-0 text-left`}
                        title="Click to view signer details"
                      >
                        {statusLabel}
                      </button>
                    ) : (
                      <span className={`text-sm font-medium ${statusTextColor}`}>{statusLabel}</span>
                    )}
                    {getDraftBadge(envelope) && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDraftBadge(envelope).color}`}>
                        {getDraftBadge(envelope).text}
                      </span>
                    )}
                  </div>

                  {/* Last modified */}
                  <div>
                    <span className="text-sm text-gray-600">{lastModified}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 relative">
                    {envelope.status === 'completed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDownloadModal(envelope)}
                        className="flex items-center gap-1 text-xs"
                        title="Download options"
                      >
                        <Download size={14} />
                        Download
                      </Button>
                    )}
                    {envelope.status === 'draft' && (() => {
                      const hasRecipients = Array.isArray(envelope.recipients) && envelope.recipients.length > 0;
                      if (!hasRecipients) {
                        // "Fields only" draft — resume directly in the field editor
                        return (
                          <Link to="/send/editor" state={{ envelopeId: envelope.id, documentTitle: envelope.title }}>
                            <Button variant="outline" size="sm" className="text-xs">Resume</Button>
                          </Link>
                        );
                      }
                      // "Recipients ready" draft — go to ReviewAndSend
                      return (
                        <Link to={`/envelopes/${envelope.id}/review`}>
                          <Button variant="outline" size="sm" className="text-xs">Resume</Button>
                        </Link>
                      );
                    })()}
                    {/* In-Progress: Remind button + dropdown menu */}
                    {envelope.status === 'sent' && (
                      <>
                        <button
                          onClick={() => handleRemind(envelope.id)}
                          disabled={remindingId === envelope.id}
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50 px-1"
                          title="Send reminder to pending signers"
                        >
                          {remindingId === envelope.id ? 'Sending…' : 'Remind'}
                        </button>
                        <div className="relative">
                          <button
                            onClick={() => setOpenMenuId(openMenuId === envelope.id ? null : envelope.id)}
                            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                            title="More actions"
                          >
                            <MoreHorizontal size={18} />
                          </button>
                          {openMenuId === envelope.id && (
                            <>
                              {/* Click-outside overlay */}
                              <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
                              <div className="absolute right-0 mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                                <button
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                  onClick={() => { setOpenMenuId(null); navigate(`/envelopes/${envelope.id}`); }}
                                >
                                  View
                                </button>
                                <button
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                  onClick={() => handleOpenEditEmail(envelope)}
                                >
                                  Edit recipient email
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </>
                    )}
                    {/* Completed / Declined: View button */}
                    {(envelope.status === 'completed' || envelope.status === 'declined') && (
                      <Link to={`/envelopes/${envelope.id}`}>
                        <Button variant="outline" size="sm" className="text-xs">View</Button>
                      </Link>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-16">
              <Send className="mx-auto text-gray-300 mb-4" size={48} />
              <p className="text-gray-500 text-base">
                {searchQuery ? 'No documents found matching your search' : 'No documents yet'}
              </p>
            </div>
          )}

          {/* Load more */}
          {!isLoading && hasMore && (
            <div className="flex justify-center border-t border-gray-200 py-3">
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => fetchEnvelopes({ append: true })}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </div>

      </div>

      {/* Download Options Modal */}
      {showDownloadModal && downloadTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Download Options</h2>
            <p className="text-sm text-gray-500 mb-4">What would you like to download?</p>
            <div className="space-y-1 mb-6">
              {[
                { value: 'signed', label: 'Signed Document' },
                { value: 'certificate', label: 'Certificate' },
                { value: 'both', label: 'Both (ZIP)' },
                { value: 'both-pdf', label: 'Both (Single PDF)' },
              ].map(opt => (
                <label
                  key={opt.value}
                  className="flex items-center gap-3 cursor-pointer p-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <input
                    type="radio"
                    name="downloadOption"
                    value={opt.value}
                    checked={downloadOption === opt.value}
                    onChange={() => setDownloadOption(opt.value)}
                    className="h-4 w-4 text-indigo-600 cursor-pointer"
                  />
                  <span className="text-sm text-gray-800">{opt.label}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowDownloadModal(false); setDownloadTarget(null); }}
              >
                Cancel
              </Button>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={handleDownloadConfirm}
              >
                Download
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Delete selected documents?</h2>
            <p className="text-sm text-gray-500 mb-6">
              You are about to delete {selectedIds.size} document{selectedIds.size > 1 ? 's' : ''}. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={isBulkLoading}>Cancel</Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleBulkDelete}
                disabled={isBulkLoading}
              >
                {isBulkLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mail Modal */}
      {showMailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Send by Email</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">To *</label>
                <Input
                  value={mailTo}
                  onChange={e => setMailTo(e.target.value)}
                  placeholder="recipient@example.com"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Subject</label>
                <Input
                  value={mailSubject}
                  onChange={e => setMailSubject(e.target.value)}
                  placeholder="Documents from VivyaSigns"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Message (optional)</label>
                <textarea
                  value={mailMessage}
                  onChange={e => setMailMessage(e.target.value)}
                  className="mt-1 w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={3}
                  placeholder="Please find the attached documents."
                />
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500">
                <p className="font-medium mb-1">Attachments ({selectedIds.size}):</p>
                {[...selectedIds].map(id => {
                  const env = envelopes.find(e => e.id === id);
                  return env ? (
                    <p key={id}>• {env.title} — {env.status === 'completed' ? 'Signed PDF' : 'Original Document'}</p>
                  ) : null;
                })}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <Button variant="outline" onClick={() => setShowMailModal(false)} disabled={isBulkLoading}>Cancel</Button>
              <Button
                onClick={handleBulkMail}
                disabled={isBulkLoading}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {isBulkLoading ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Recipient Email — Enter New Email Modal */}
      {showEditEmailModal && editEmailTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Edit recipient email</h2>
            <p className="text-sm text-gray-500 mb-4">
              {editEmailTarget.recipient.display_name} &lt;{editEmailTarget.recipient.email}&gt;
            </p>
            <label className="text-sm font-medium text-gray-700">New email address</label>
            <Input
              value={newEmailValue}
              onChange={e => setNewEmailValue(e.target.value)}
              placeholder="new@example.com"
              className="mt-1 mb-4"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleEmailInputNext(); }}
            />
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setShowEditEmailModal(false); setEditEmailTarget(null); }}>Cancel</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleEmailInputNext}>Next</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Recipient Email — Review Changes Modal */}
      {showReviewChangesModal && editEmailTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Review changes</h2>
            <p className="text-sm text-gray-500 mb-4">Recipient edited: 1</p>
            <div className="border rounded-lg p-4 space-y-3 text-sm bg-gray-50 mb-5">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Previous</p>
                <p className="font-medium text-gray-800">{editEmailTarget.recipient.display_name}</p>
                <p className="text-gray-600">{editEmailTarget.recipient.email}</p>
              </div>
              <div className="border-t pt-3">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">New</p>
                <p className="font-medium text-gray-800">{editEmailTarget.recipient.display_name}</p>
                <p className="text-gray-600">{newEmailValue}</p>
                <span className="inline-block mt-1 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">Signer</span>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setShowReviewChangesModal(false); setShowEditEmailModal(true); }} disabled={isSendingEmailUpdate}>Edit</Button>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={handleSendEmailUpdate}
                disabled={isSendingEmailUpdate}
              >
                {isSendingEmailUpdate ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Signer Details Popup (SignEasy-style) ── */}
      {signerPopup && (
        <>
          {/* Transparent click-outside overlay */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setSignerPopup(null)}
          />
          {/* Popup card */}
          <div
            className="fixed z-50 bg-white rounded-xl shadow-2xl border border-gray-100 w-72 overflow-hidden"
            style={{ top: signerPopup.top, left: signerPopup.left }}
          >
            {isLoadingPopup || !signerPopup.data ? (
              /* Loading skeleton */
              <div className="p-4 space-y-2">
                <div className="h-4 bg-gray-100 rounded animate-pulse w-3/4" />
                <div className="h-3 bg-gray-100 rounded animate-pulse w-full" />
                <div className="h-3 bg-gray-100 rounded animate-pulse w-2/3" />
                <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2" />
              </div>
            ) : (() => {
              const { envelope: env, org } = signerPopup.data;
              const signers = (env.recipients || []).filter(
                r => !['cc', 'copy'].includes((r.role || '').toLowerCase())
              );
              const initiatedOn = env.created_at
                ? new Date(env.created_at).toLocaleString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric',
                    hour: 'numeric', minute: '2-digit',
                  })
                : '—';
              const expiresOn = env.expires_at
                ? new Date(env.expires_at).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric',
                  })
                : '—';
              const signerStatusLabel = (status) => {
                switch ((status || '').toLowerCase()) {
                  case 'completed': return 'Signed';
                  case 'declined':  return 'Declined';
                  case 'pending':
                  default:          return 'Waiting for signature';
                }
              };
              return (
                <>
                  {/* Sender (You) section */}
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {org.name || org.admin_email} <span className="font-normal text-gray-500">(You)</span>
                        </p>
                        <p className="text-xs text-gray-500 truncate">{org.admin_email}</p>
                        <p className="text-xs text-gray-400 mt-1">Initiated on {initiatedOn}</p>
                        <p className="text-xs text-gray-400">Expires on {expiresOn}</p>
                      </div>
                    </div>
                  </div>

                  {/* Signers section */}
                  <div className="p-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Signers</p>
                    <div className="space-y-3">
                      {signers.length === 0 ? (
                        <p className="text-xs text-gray-400">No signers found</p>
                      ) : signers.map((signer, i) => (
                        <div key={signer.id || i} className="flex items-start gap-3">
                          <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {signer.display_name || signer.email}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{signer.email}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{signerStatusLabel(signer.status)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </>
      )}

    </Layout>
  );
};

export default Envelopes;